import { useState, useEffect, useRef, useCallback } from 'react';
import Raven from 'raven-js';
import styled from 'foundations-theming/utils';
import { Image } from 'foundations-components/transitional/components/Image';
import { Link } from 'foundations-components/transitional/components/Link';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
// @ts-expect-error not migrated
import WistiaVideoPlayer from 'ui-addon-video-player/components/WistiaVideoPlayer';
import { H2 } from 'foundations-components/transitional/components/H2';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import SkeletonText from 'conversations-skeleton-state/components/SkeletonText';
import { parseUrl } from 'hub-http/helpers/url';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import RouterLink from 'ui-addon-react-router-dom/RouterLink';
import { BREAKPOINTS } from 'foundations-components/transitional/components/Grid';
import { Button } from 'foundations-components/transitional/components/Button';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import { DownloadIcon } from 'foundations-assets/icons/components/DownloadIcon';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { useHistory, useLocation } from 'react-router-dom';
import { startConfetti } from '../../../utils/confetti';
import { getAwardSharingPath } from '../../../utils/RoutingUtils';
import { isExpiringSoon } from '../../../features/myTeam/utils/dateTimeUtils';

// @ts-expect-error image
import certBackgroundShapeUrl from 'bender-url!../../../../images/blob.svg';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { LANGUAGE_STRINGS } from '../../Languages';
import { PROFILE_URL_FRAGMENT } from '../../../constants/Navigation';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const CONFETTI_TIME = 600000;

// @bend-expect trellis-tokenized
const CertificateBackground = styled.div.withConfig({
  displayName: "TrackAwardModal__CertificateBackground"
})(["background-image:url(", ");background-position:top;background-repeat:no-repeat;background-size:contain;width:100%;max-width:750px;padding:", ";margin:0;@media (min-width:", "px){padding:", " ", " ", ";margin:-", " ", " -", ";}"], props => props.src, ({
  theme
}) => theme.space['200'], BREAKPOINTS.sm, ({
  theme
}) => theme.space['700'], ({
  theme
}) => theme.space['1500'], ({
  theme
}) => theme.space['1500'], ({
  theme
}) => theme.space['700'], ({
  theme
}) => theme.space['1500'], ({
  theme
}) => theme.space['1500']);
function getIsSubheadingReady(showLinkedTrackAwardInfo, track, progressLoading) {
  if (!showLinkedTrackAwardInfo && track && track.get('name')) {
    return true;
  }
  if (showLinkedTrackAwardInfo && !progressLoading) {
    return true;
  }
  return false;
}
const TrackAwardModal = ({
  award,
  examAttempt = {
    score: 0
  },
  firstName,
  onClickShareThis,
  onClose = () => {},
  onDownloadCertificate = () => {},
  onMount = () => {},
  track,
  linkedTrackId = null,
  linkedTrackLanguage = null,
  progressLoading
}) => {
  const {
    isMobile
  } = useMediaQuery();
  const history = useHistory();
  const location = useLocation();
  const [certObjectUrl, setCertObjectUrl] = useState();
  const [certFileName, setCertFileName] = useState();
  const cleanupConfettiRef = useRef();
  const certImageBlobForIERef = useRef();
  const captureBreadcrumb = useCallback((message, data = {}) => {
    Raven.captureBreadcrumb({
      message,
      category: 'TrackAwardModal',
      data: Object.assign({
        award
      }, data)
    });
  }, [award]);
  const setCertImageState = useCallback((blob, src) => {
    setCertObjectUrl(URL.createObjectURL(blob));
    setCertFileName(parseUrl(src).path.replace(/^.*\//, ''));
  }, []);
  useEffect(() => {
    if (new Date().getTime() - award.awardedAt < CONFETTI_TIME) {
      cleanupConfettiRef.current = startConfetti();
    }
    onMount();
    captureBreadcrumb('componentDidMount');
    return () => {
      if (certObjectUrl) {
        URL.revokeObjectURL(certObjectUrl);
      }
      if (cleanupConfettiRef.current) {
        cleanupConfettiRef.current();
        cleanupConfettiRef.current = undefined;
      }
      captureBreadcrumb('componentWillUnmount');
    };
  }, [award.awardedAt, onMount, captureBreadcrumb, certObjectUrl]);
  useEffect(() => {
    captureBreadcrumb('award changed', {
      award
    });
  }, [award, captureBreadcrumb]);
  const handleDownloadCertificate = useCallback(event => {
    const navigator = window.navigator;
    if (navigator.msSaveBlob && certImageBlobForIERef.current) {
      navigator.msSaveBlob(certImageBlobForIERef.current, event.currentTarget.getAttribute('download'));
    }
    onDownloadCertificate === null || onDownloadCertificate === void 0 || onDownloadCertificate(event);
  }, [onDownloadCertificate]);
  const handleImgLoadError = useCallback(({
    currentTarget
  }) => {
    const extra = {
      category: currentTarget.dataset.category,
      src: currentTarget.src,
      award,
      visibility: award === null || award === void 0 ? void 0 : award.visibility,
      certificateUrl: award === null || award === void 0 ? void 0 : award.certificate,
      userId: award === null || award === void 0 ? void 0 : award.userId
    };
    captureBreadcrumb('onImgLoadError', extra);

    // Always log the error first
    Raven.captureMessage('TrackAwardModal: Certificate image failed to load', {
      level: 'error',
      extra
    });
  }, [award, captureBreadcrumb]);
  const handleLoadCertImage = useCallback(({
    currentTarget
  }) => {
    if (!certObjectUrl) {
      const cnv = document.createElement('canvas');
      cnv.height = currentTarget.naturalHeight;
      cnv.width = currentTarget.naturalWidth;
      const ctx = cnv.getContext('2d');
      if (ctx) {
        ctx.drawImage(currentTarget, 0, 0);
      }
      if (cnv.toBlob) {
        cnv.toBlob(blob => {
          if (blob) {
            setCertImageState(blob, currentTarget.src);
          }
        });
      } else {
        // IE fallback
        const canvasWithMsToBlob = cnv;
        if (canvasWithMsToBlob.msToBlob) {
          setTimeout(() => {
            // Delayed to allow canvas.drawImage to fully complete
            certImageBlobForIERef.current = canvasWithMsToBlob.msToBlob();
            if (certImageBlobForIERef.current) {
              setCertImageState(certImageBlobForIERef.current, currentTarget.src);
            }
          });
        }
      }
      captureBreadcrumb('onLoadCertImage', {
        src: currentTarget.src,
        height: cnv.height,
        width: cnv.width
      });
    }
  }, [certObjectUrl, setCertImageState, captureBreadcrumb]);

  // for tracks where the cert has been awarded in a different language
  // we show a modified subheading
  const renderSubHeading = () => {
    const showLinkedTrackAwardInfo = linkedTrackId !== null;
    const correctQuestionCount = examAttempt.score;
    const totalQuestionCount = examAttempt.questionIds && examAttempt.questionIds.length || 0;
    const isSubheadingReady = getIsSubheadingReady(showLinkedTrackAwardInfo, track, progressLoading);
    const scoreText = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.exams.finished.yourScoreWas",
      options: {
        correctQuestionCount,
        totalQuestionCount
      }
    });
    const courseName = track.get('name');
    const language = track.get('language');
    if (!isSubheadingReady) {
      return /*#__PURE__*/_jsx(Flex, {
        alignItems: "center",
        flexDirection: "column",
        justifyContent: "center",
        marginBlockEnd: "400",
        children: /*#__PURE__*/_jsxs(View, {
          inlineSize: "50%",
          children: [/*#__PURE__*/_jsx(SkeletonText, {}), /*#__PURE__*/_jsx(SkeletonText, {})]
        })
      });
    }

    // show current track exam info
    if (!showLinkedTrackAwardInfo) {
      return /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        flexDirection: "column",
        justifyContent: "center",
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: 'learningCenter.exams.finished.passedSubheading',
          options: {
            courseName
          }
        }), scoreText]
      });
    }

    // show linked track exam info
    if (linkedTrackLanguage) {
      return /*#__PURE__*/_jsx(Flex, {
        alignItems: "center",
        flexDirection: "column",
        justifyContent: "center",
        paddingInline: "300",
        children: /*#__PURE__*/_jsxs(Text, {
          textAlign: "center",
          typography: "body-125",
          children: [/*#__PURE__*/_jsx(View, {
            marginBlockEnd: "300",
            maxInlineSize: "500px",
            children: /*#__PURE__*/_jsx(FormattedReactMessage, {
              "data-test-id": "track-award-modal-subheading",
              message: "learningCenter.exams.finished.passedInLinkedLanguage",
              options: {
                courseName,
                linkedLanguage: LANGUAGE_STRINGS[linkedTrackLanguage],
                language: LANGUAGE_STRINGS[language]
              }
            })
          }), scoreText]
        })
      });
    }
  };
  const renderCertificateAndVideoContent = () => {
    const passVideo = track === null || track === void 0 ? void 0 : track.getIn(['passVideo', 'url']);
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [!!award.certificate &&
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      _jsx(CertificateBackground, {
        src: certBackgroundShapeUrl,
        children: /*#__PURE__*/_jsx(Image, {
          crossOrigin: "anonymous",
          "data-category": "cert",
          onError: handleImgLoadError,
          onLoad: handleLoadCertImage,
          responsive: true,
          src: award.certificate
        })
      }), !!passVideo && /*#__PURE__*/_jsx(View, {
        inlineSize: "95%",
        maxInlineSize: "746px",
        padding: "500",
        position: "relative",
        children: /*#__PURE__*/_jsx(WistiaVideoPlayer, {
          videoId: passVideo
        })
      })]
    });
  };
  const renderSharingSection = () => {
    const trackId = track.get('id');
    const language = track.get('language');
    const hasAwardForLinkedTrack = linkedTrackId !== null;
    const showRetakeButton = (award === null || award === void 0 ? void 0 : award.expiresAt) && trackId && isExpiringSoon(award.expiresAt);
    return /*#__PURE__*/_jsx(View, {
      marginBlockEnd: isMobile ? '0' : '1500',
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        flexDirection: "column",
        children: [/*#__PURE__*/_jsxs(Flex, {
          flexDirection: isMobile ? 'column' : 'row',
          gap: "200",
          marginBlockStart: "500",
          marginInline: "0",
          children: [!!showRetakeButton && /*#__PURE__*/_jsx(RouterLink, {
            to: `/tracks/${trackId}/exam`,
            children: /*#__PURE__*/_jsx(Button, {
              use: "primary",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.trackAwardModal.retakeExam"
              })
            })
          }), /*#__PURE__*/_jsxs(Flex, {
            gap: "200",
            children: [/*#__PURE__*/_jsx(Button, {
              "data-test-id": "share-this-button",
              onClick: () => {
                onClickShareThis();
                history.replace(getAwardSharingPath(trackId), {
                  backFromAwardShare: location
                });
              },
              paddingInline: "200",
              responsive: false,
              use: "tertiary-light",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.trackAwardModal.shareThisCertificate"
              })
            }), /*#__PURE__*/_jsx(IconButton, {
              "data-test-id": "award-download-button",
              disabled: !certObjectUrl,
              download: certFileName,
              href: certObjectUrl,
              marginInlineStart: "0",
              onClick: handleDownloadCertificate,
              paddingBlock: "200",
              tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.trackAwardModal.downloadCertificate"
              }),
              children: /*#__PURE__*/_jsx(DownloadIcon, {
                size: "500"
              })
            })]
          })]
        }), hasAwardForLinkedTrack && /*#__PURE__*/_jsx(RouterLink, {
          paddingBlockStart: "400" /* TODO double check if this is working as intended */,
          to: `/tracks/${trackId}/exam`,
          children: /*#__PURE__*/_jsx(FormattedReactMessage, {
            message: "learningCenter.exams.finished.linkedExamCTA",
            options: {
              language: LANGUAGE_STRINGS[language]
            }
          })
        })]
      })
    });
  };
  const renderAchievementsSection = () => {
    return /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      flexDirection: "column",
      paddingBlockStart: isMobile ? '700' : '900',
      paddingInline: isMobile ? '400' : '800',
      children: [/*#__PURE__*/_jsx(H2, {
        marginBlockEnd: "200",
        textAlign: "center",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.trackAwardModal.viewYourAchievements"
        })
      }), /*#__PURE__*/_jsx(Text, {
        marginBlockEnd: "400",
        textAlign: "center",
        children: /*#__PURE__*/_jsx(View, {
          maxInlineSize: "275px",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.trackAwardModal.checkOutAllAchievements"
          })
        })
      }), /*#__PURE__*/_jsx(RouterLink, {
        to: PROFILE_URL_FRAGMENT,
        children: /*#__PURE__*/_jsx(Button, {
          use: "primary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.trackAwardModal.profileCTAText"
          })
        })
      })]
    });
  };
  const endOfTrackCtaText = track.get('endOfTrackCtaText');
  const endOfTrackCtaLink = track.get('endOfTrackCtaLink');
  if (!examAttempt) return null;
  return /*#__PURE__*/_jsxs(Modal, {
    "data-test-id": "track-award-modal",
    use: "conversational",
    width: 1250,
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        "aria-level": 1,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.exams.finished.successHeading",
          options: {
            firstName
          }
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      })]
    }), /*#__PURE__*/_jsx(DialogBody, {
      padding: isMobile ? '0' : undefined,
      children: /*#__PURE__*/_jsxs(View, {
        marginBlockStart: "400",
        minBlockSize: isMobile ? undefined : '700px',
        paddingBlockEnd: "400",
        children: [/*#__PURE__*/_jsxs(View, {
          marginBlockEnd: "800",
          textAlign: "center",
          children: [renderSubHeading(), !!endOfTrackCtaText && !!endOfTrackCtaLink && /*#__PURE__*/_jsx(Link, {
            href: endOfTrackCtaLink,
            target: "_blank",
            children: /*#__PURE__*/_jsx("h3", {
              children: endOfTrackCtaText
            })
          })]
        }), /*#__PURE__*/_jsxs(Grid, {
          children: [/*#__PURE__*/_jsx(GridItem, {
            size: {
              xs: 12,
              md: 7
            },
            children: /*#__PURE__*/_jsxs(Flex, {
              alignItems: "center",
              flexDirection: "column",
              marginInline: "auto",
              minBlockSize: "300px",
              children: [renderCertificateAndVideoContent(), renderSharingSection()]
            })
          }), /*#__PURE__*/_jsx(GridItem, {
            size: {
              xs: 12,
              md: 5
            },
            children: renderAchievementsSection()
          })]
        })]
      })
    })]
  });
};
export default TrackAwardModal;